import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import request from "../../../utilities/requests";

const MixerFilters = ({
  open,
  onClose,
  eventId,
  onViewResults,
  onClearAllFilters,
}) => {
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState([]);
  const [error, setError] = useState(null);
  const [selectedValues, setSelectedValues] = useState({});
  const [firstNameFilter, setFirstNameFilter] = useState("");

  const fetchFilters = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await request(
        `pairingevent/mixerfilters?eventId=${eventId}`,
      );
      if (result.success) {
        setFilters(result.filters);
      }
    } catch (err) {
      console.error("Error fetching filters:", err);
      setError("Failed to load filters");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChange = (questionId, value, rangeType = null) => {
    setSelectedValues((prev) => {
      // Range object support (preferredAge etc)
      if (rangeType) {
        return {
          ...prev,
          [questionId]: {
            ...(prev[questionId] || {}),
            [rangeType]: value,
          },
        };
      }

      // Normal filters
      return {
        ...prev,
        [questionId]: value,
      };
    });
  };

  const handleClearFilters = () => {
    setSelectedValues({});
    setFirstNameFilter("");
    onClearAllFilters();
  };

  const handleViewResults = () => {
    if (onViewResults) {
      const payloadFilters = {
        ...selectedValues,
        ...(firstNameFilter ? { firstName: firstNameFilter } : {}),
      };

      onViewResults(payloadFilters);
    }
    onClose();
  };

  // Load filters only once (filters never change)
  useEffect(() => {
    if (open && filters.length === 0) {
      fetchFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" >
      <DialogTitle>Filters</DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2 }}>
      
        <Box sx={{ mb: 2, pt:2 }}>
          <TextField
            fullWidth
            label="Search by First Name"
            value={firstNameFilter}
            onChange={(e) => setFirstNameFilter(e.target.value)}
          />
        </Box>

        <Box sx={{ minHeight: 200 }}>
          {loading && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 150,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {!loading && error && <Typography color="error">{error}</Typography>}

          {!loading && !error && filters.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No filters available.
            </Typography>
          )}

          {!loading && !error && filters.length > 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {filters.map((filter) => {
                const questionId = String(filter._id);

                // RANGE TYPE → dynamically build menu items
                if (filter.questionType === "range") {
                  const rangeValues = [];
                  for (
                    let i = filter.lowerRangeValue;
                    i <= filter.upperRangeValue;
                    i++
                  ) {
                    rangeValues.push(i);
                  }

                  // SPECIAL CASE → preferredAge renders LOWER + UPPER selects inline
                  if (filter.variant === "preferredAge") {
                    const rangeValue = selectedValues[questionId] || {};

                    return (
                      <Box
                        key={questionId}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                          pt: 2.5,
                        }}
                      >
                        <TextField
                          select
                          fullWidth
                          label="Preferred Age (Min)"
                          value={rangeValue.lower ?? ""}
                          onChange={(e) =>
                            handleSelectChange(
                              questionId,
                              e.target.value,
                              "lower",
                            )
                          }
                        >
                          {rangeValues.map((val) => (
                            <MenuItem key={`min-${val}`} value={val}>
                              {val}
                            </MenuItem>
                          ))}
                        </TextField>

                        <TextField
                          select
                          fullWidth
                          label="Preferred Age (Max)"
                          value={rangeValue.upper ?? ""}
                          onChange={(e) =>
                            handleSelectChange(
                              questionId,
                              e.target.value,
                              "upper",
                            )
                          }
                        >
                          {rangeValues.map((val) => (
                            <MenuItem key={`max-${val}`} value={val}>
                              {val}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    );
                  }

                  // DEFAULT RANGE → single select
                  return (
                    <FormControl key={questionId} fullWidth variant="outlined">
                      <InputLabel>{filter.question}</InputLabel>
                      <Select
                        label={filter.question}
                        value={selectedValues[questionId] || ""}
                        onChange={(e) =>
                          handleSelectChange(questionId, e.target.value)
                        }
                      >
                        {rangeValues.map((val) => (
                          <MenuItem key={val} value={val}>
                            {val}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  );
                }

                // CHOICE / RELATED / MULTIPLE CHOICE / YESNO
                return (
                  <FormControl key={questionId} fullWidth variant="outlined">
                    <InputLabel>{filter.question}</InputLabel>
                    <Select
                      label={filter.question}
                      value={
                        selectedValues[questionId] ||
                        (filter.questionType === "multipleChoice" ? [] : "")
                      }
                      multiple={filter.questionType === "multipleChoice"}
                      onChange={(e) =>
                        handleSelectChange(questionId, e.target.value)
                      }
                    >
                      {filter.questionType === "yesno"
                        ? [
                            { label: "Yes", value: "yes" },
                            { label: "No", value: "no" },
                          ].map((choice) => (
                            <MenuItem key={choice.value} value={choice.value}>
                              {choice.label}
                            </MenuItem>
                          ))
                        : filter.choices?.map((choice) => (
                            <MenuItem
                              key={String(choice._id)}
                              value={choice.value}
                            >
                              {choice.label}
                            </MenuItem>
                          ))}
                    </Select>
                  </FormControl>
                );
              })}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
        <Button
          onClick={handleClearFilters}
          disabled={!Object.keys(selectedValues).length && !firstNameFilter}
        >
          Clear Filters
        </Button>

        <Button onClick={handleViewResults} variant="contained">
          View Results
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MixerFilters;
